// NE login SDK — browser/client side (framework-agnostic, no Node deps).
//
// Talks to your server's /api/ne/* routes (which run the SDK server side).
// CARSI is intentionally pluggable: pass an `openDialog` opener so this stays
// independent of Office.js / any specific host.

import * as C from "./constants.js";

export { C as NE_CONSTANTS };

let STORAGE_KEY = "ne:auth:v1";
/** Override the localStorage key (e.g. to namespace per product). */
export function setNeStorageKey(key) {
  if (key) STORAGE_KEY = key;
}

export function loadNeAuth() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (!a || !a.token || !a.user) return null;
    if (typeof a.useBuiltin !== "boolean") a.useBuiltin = true;
    return a;
  } catch {
    return null;
  }
}
export function saveNeAuth(auth) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  } catch {
    /* ignore */
  }
}
export function clearNeAuth() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Account login via your server (server encrypts + calls NE). */
export async function neAccountLogin(username, password, opts = {}) {
  const basePath = opts.basePath || "/api/ne";
  try {
    const resp = await fetch(`${basePath}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.ok) return { ok: false, msg: data.msg || `登录失败（${resp.status}）` };
    return { ok: true, token: data.token, user: data.user };
  } catch (e) {
    return { ok: false, msg: e && e.message ? e.message : String(e) };
  }
}

/**
 * CARSI (SSO) login. `openDialog(url)` must open `url`, wait for the callback
 * page to post a JSON string back, and resolve with that string (or reject).
 * For Office add-ins, wrap Office.context.ui.displayDialogAsync; for plain web,
 * wrap window.open + postMessage. Keeps this SDK host-agnostic.
 */
export async function neCarsiLogin(openDialog, opts = {}) {
  const basePath = opts.basePath || "/api/ne";
  const origin = opts.origin || (typeof window !== "undefined" ? window.location.origin : "");
  const startUrl = `${origin}${basePath}/carsi/start`;
  try {
    const message = await openDialog(startUrl);
    const payload = typeof message === "string" ? JSON.parse(message) : message;
    if (payload && payload.ok) return { ok: true, token: payload.token, user: payload.user };
    return { ok: false, msg: (payload && payload.msg) || "CARSI 登录失败" };
  } catch (e) {
    return { ok: false, msg: e && e.message ? e.message : String(e) };
  }
}

/** OpenAI-compatible config for the built-in model (mirrors gatewayConfig). */
export function neConfig(token, temperature, opts = {}) {
  return {
    baseUrl: opts.base || C.NE_GATEWAY_BASE,
    apiKey: `${opts.appSource || C.NE_APP_SOURCE}##${token}`,
    model: opts.model || C.NE_BUILTIN_MODEL_ID,
    temperature: typeof temperature === "number" ? temperature : 0.7,
    viaServer: true,
  };
}

/** Fetch available models from the NE gateway via the server proxy. */
export async function neFetchModels(token, opts = {}) {
  const basePath = opts.basePath || "/api/ne";
  try {
    const resp = await fetch(`${basePath}/models?aut=${encodeURIComponent(token)}`);
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.ok) return { ok: false, msg: data.msg || `获取模型列表失败（${resp.status}）` };
    return { ok: true, models: data.models || [] };
  } catch (e) {
    return { ok: false, msg: e?.message || String(e) };
  }
}

/** Detect quota/balance errors so the UI can prompt the user to recharge. */
export function isQuotaError(message) {
  const m = (message || "").toLowerCase();
  return (
    m.includes("quota") ||
    m.includes("insufficient") ||
    m.includes("balance") ||
    m.includes("额度") ||
    m.includes("余额") ||
    m.includes("欠费") ||
    m.includes("token 不足") ||
    m.includes("402")
  );
}
